/**
 * API back-office vie scolaire : présence, conduite, incidents santé.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface SeanceItem {
  id: number;
  date: string;
  heure_debut: string;
  heure_fin: string;
  classe: string;
  matiere: string;
  code_matiere?: string;
  salle: string;
  formateur: string;
  statut: 'prevue' | 'en_cours' | 'tenue' | 'annulee' | 'reportee';
  appel_fait: boolean;
  formateur_present: boolean;
  tronc_commun_groupe?: string;
  est_rattrapage?: boolean;
  seance_origine_id?: number | null;
  nb_present?: number;
  nb_absent?: number;
  nb_justifie?: number;
  nb_incident?: number;
}

export interface AppelEtudiantItem {
  matricule: string;
  nom: string;
  prenoms: string;
  classe?: string;
  statut: 'present' | 'absent' | 'retard' | 'justifie' | 'incident';
  minutes_retard: number;
  motif: string;
  commentaire: string;
  justification_par_parent: boolean;
  justification_validee: boolean;
}

export interface AppelTroncCommunResponse {
  tronc_commun_groupe: string;
  seances: Array<{ id: number; classe: string; appel_fait: boolean }>;
  matiere: string;
  code_matiere: string;
  formateur: string;
  date: string;
  heure_debut: string;
  heure_fin: string;
  salle: string;
  classes: string[];
  etudiants: AppelEtudiantItem[];
}

export const vieScolaireApi = createApi({
  reducerPath: 'vieScolaireApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Seances', 'Seance'],
  endpoints: (builder) => ({
    listerSeances: builder.query<{ results: SeanceItem[]; total: number }, {
      depuis?: string; jusqua?: string; classe?: string; statut?: string;
    } | void>({
      query: (params) => ({ url: '/vie-scolaire/seances/', params: params ?? undefined }),
      providesTags: ['Seances'],
    }),

    detailAppel: builder.query<{
      seance: SeanceItem & {
        tronc_commun_groupe?: string;
        tronc_commun_classes?: string[];
      };
      etudiants: AppelEtudiantItem[];
    }, number>({
      query: (id) => `/vie-scolaire/seances/${id}/appel/`,
      providesTags: (_r, _e, id) => [{ type: 'Seance', id }],
    }),

    // Appel tronc commun (étudiants agrégés)
    detailAppelTroncCommun: builder.query<AppelTroncCommunResponse, { date: string; groupe: string }>({
      query: ({ date, groupe }) => ({
        url: '/vie-scolaire/appel-tronc-commun/',
        params: { date, groupe },
      }),
      providesTags: ['Seances'],
    }),
    validerAppelTroncCommun: builder.mutation<{ success: boolean; nb_traites: number; nb_seances: number }, {
      date: string;
      groupe: string;
      presences: Array<{ matricule: string; classe: string; statut: string; minutes_retard?: number; motif?: string; commentaire?: string }>;
    }>({
      query: (body) => ({
        url: '/vie-scolaire/appel-tronc-commun/valider/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    // Séance à la volée (oubli EDT côté scolarité)
    creerSeanceALaVolee: builder.mutation<{ id: number; deja_existante?: boolean }, {
      date: string; classe: string; libelle_matiere: string;
      heure_debut: string; heure_fin: string;
      code_matiere?: string; formateur_nom?: string; salle?: string;
    }>({
      query: (body) => ({ url: '/vie-scolaire/seances/creer/', method: 'POST', body }),
      invalidatesTags: ['Seances'],
    }),

    // Supprime ou annule une séance
    supprimerSeance: builder.mutation<{ success: boolean; mode: 'supprimee' | 'annulee' }, number>({
      query: (id) => ({ url: `/vie-scolaire/seances/${id}/supprimer/`, method: 'DELETE' }),
      invalidatesTags: ['Seances'],
    }),

    // Reporte une séance vers une autre date (cours de rattrapage)
    reporterSeance: builder.mutation<{
      success: boolean;
      nb_reportes: number;
      nb_rattrapages_crees: number;
      rattrapages: Array<{ id: number; classe: string; date: string; heure_debut: string; heure_fin: string }>;
    }, {
      seanceId: number;
      date_nouvelle: string;
      heure_debut?: string;
      heure_fin?: string;
      salle?: string;
      motif?: string;
    }>({
      query: ({ seanceId, ...body }) => ({
        url: `/vie-scolaire/seances/${seanceId}/reporter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    // Appel tronc commun IMPLICITE (mêmes jour/heure/matière/formateur sans groupe explicite)
    detailAppelTroncCommunImplicite: builder.query<{
      mode: 'implicite';
      seances: Array<{ id: number; classe: string; appel_fait: boolean }>;
      matiere: string;
      formateur: string;
      date: string;
      heure_debut: string;
      heure_fin: string;
      salle: string;
      classes: string[];
      etudiants: AppelEtudiantItem[];
      seance_ids: number[];
    }, { date: string; heure_debut: string; matiere: string; formateur?: string }>({
      query: (params) => ({
        url: '/vie-scolaire/appel-tronc-commun/implicite/',
        params,
      }),
      providesTags: ['Seances'],
    }),

    validerAppelTroncCommunImplicite: builder.mutation<{ success: boolean; nb_traites: number; nb_seances: number }, {
      seance_ids: number[];
      presences: Array<{ matricule: string; classe: string; statut: string; minutes_retard?: number; motif?: string; commentaire?: string }>;
    }>({
      query: (body) => ({
        url: '/vie-scolaire/appel-tronc-commun/implicite/valider/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    // Matières disponibles pour saisir un EDT (filtrées par classe + période)
    matieresDisponiblesEdt: builder.query<{
      niveau: string; periode: string; semestre_global: number;
      option: string | null; annee_academique: string | null;
      results: Array<{
        id: number; code_matiere: string; nom_matiere: string;
        niveau: string; semestre: number; option: string;
        formateurs: Array<{ id: number; nom: string }>;
      }>;
      total: number;
    }, { niveau?: string; periode?: 'pair' | 'impair'; option?: string; classe?: string }>({
      query: (params) => ({ url: '/vie-scolaire/edt/matieres-disponibles/', params }),
    }),

    // EDT — horaires standards selon niveau (L1/L2/L3)
    horairesStandards: builder.query<{
      niveau: string;
      creneaux: Array<{ heure_debut: string; heure_fin: string }>;
    }, { niveau?: string; jour?: string } | void>({
      query: (params) => ({ url: '/vie-scolaire/edt/horaires-standards/', params: params ?? undefined }),
    }),

    // EDT — copier vers d'autres classes
    copierEdt: builder.mutation<{ tronc_commun_groupe: string; crees: any[]; classes_ignorees: string[] }, {
      edtId: number; classes: string[];
    }>({
      query: ({ edtId, classes }) => ({
        url: `/vie-scolaire/edt/${edtId}/copier/`,
        method: 'POST',
        body: { classes },
      }),
      invalidatesTags: ['Seances'],
    }),

    // EDT — déplacer (jour + horaires), propage au tronc commun par défaut
    deplacerEdt: builder.mutation<{ success: boolean; propage: boolean; nb_creneaux_modifies: number }, {
      edtId: number; jour?: string; heure_debut?: string; heure_fin?: string;
      salle?: string; propager?: boolean;
    }>({
      query: ({ edtId, ...body }) => ({
        url: `/vie-scolaire/edt/${edtId}/deplacer/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    validerAppel: builder.mutation<{ success: boolean; nb_traites: number }, {
      seanceId: number;
      presences: Array<{ matricule: string; statut: string; minutes_retard?: number; commentaire?: string }>;
      formateur_present?: boolean;
      formateur_motif_absence?: string;
    }>({
      query: ({ seanceId, ...body }) => ({
        url: `/vie-scolaire/seances/${seanceId}/appel/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    declarerIncident: builder.mutation<{ success: boolean; id: number }, {
      matricule: string; type?: string; gravite?: string; description: string;
      lieu?: string; seance_id?: number; suite_donnee?: string; parents_prevenus?: boolean;
    }>({
      query: (body) => ({
        url: '/vie-scolaire/incidents/',
        method: 'POST',
        body,
      }),
    }),

    listerConduite: builder.query<{
      matricule: string;
      niveau: { cle: string; libelle: string };
      score: number;
      results: any[];
      total: number;
    }, string>({
      query: (matricule) => ({
        url: '/vie-scolaire/conduite/',
        params: { matricule },
      }),
      providesTags: (_r, _e, m) => [{ type: 'Seances', id: `conduite-${m}` }],
    }),
    creerConduite: builder.mutation<{ success: boolean; id: number }, {
      matricule: string; categorie?: string; type?: string; poids?: number;
      commentaire?: string; date_observation?: string;
    }>({
      query: (body) => ({
        url: '/vie-scolaire/conduite/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, body) => [{ type: 'Seances', id: `conduite-${body.matricule}` }],
    }),

    // EDT régulier
    listerEdt: builder.query<{ results: any[] }, { classe?: string; jour?: string } | void>({
      query: (params) => ({ url: '/vie-scolaire/edt/', params: params ?? undefined }),
      providesTags: ['Seances'],
    }),
    creerEdt: builder.mutation<any, any>({
      query: (body) => ({ url: '/vie-scolaire/edt/', method: 'POST', body }),
      invalidatesTags: ['Seances'],
    }),
    updateEdt: builder.mutation<any, { id: number; data: any }>({
      query: ({ id, data }) => ({ url: `/vie-scolaire/edt/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['Seances'],
    }),
    deleteEdt: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({ url: `/vie-scolaire/edt/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Seances'],
    }),
    genererSeances: builder.mutation<{
      success: boolean; semaines: number; creees: number; existantes: number;
    }, { semaines?: number; classe?: string }>({
      query: (body) => ({ url: '/vie-scolaire/edt/generer-seances/', method: 'POST', body }),
      invalidatesTags: ['Seances'],
    }),

    // Justifications
    listerJustifications: builder.query<{
      results: any[]; nb_en_attente: number;
    }, { statut?: 'en_attente' | 'validees' | 'toutes' } | void>({
      query: (params) => ({ url: '/vie-scolaire/justifications/', params: params ?? undefined }),
      providesTags: ['Seances'],
    }),
    traiterJustification: builder.mutation<{ success: boolean; validee: boolean }, {
      id: number; action: 'valider' | 'rejeter'; commentaire?: string;
    }>({
      query: ({ id, ...body }) => ({
        url: `/vie-scolaire/justifications/${id}/traiter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Seances'],
    }),

    // Formateur
    mesSeancesFormateur: builder.query<{
      mon_nom: string; periode: { depuis: string; jusqua: string };
      total: number; results: any[];
    }, { depuis?: string; jusqua?: string } | void>({
      query: (params) => ({ url: '/vie-scolaire/formateur/mes-seances/', params: params ?? undefined }),
      providesTags: ['Seances'],
    }),

    // Fiche étudiant 360° (présences + EDT + paiements + résultats + conduite)
    ficheEtudiant: builder.query<{
      identite: {
        matricule: string; nom: string; prenoms: string; sexe: string;
        date_naissance: string | null; age: number | null;
        photo_url: string | null;
        classe: string; niveau: string; option: string; pole: string; parcours: string;
        telephone: string; email: string; type_formation: string;
      };
      presences: {
        periode: { depuis: string; jusqua: string };
        total_seances: number;
        nb_par_statut: {
          present: number; absent: number; retard: number;
          justifie: number; incident: number;
        };
        taux_presence: number;
        par_jour: Array<{
          date: string;
          statut_dominant: 'present' | 'absent' | 'retard' | 'justifie' | 'incident';
          nb_seances: number;
          detail: Array<{
            seance_id: number; libelle_matiere: string;
            heure_debut: string; heure_fin: string; classe: string;
            statut: string; minutes_retard: number;
            motif_justification: string; justification_validee: boolean;
          }>;
        }>;
      };
      emploi_du_temps: Array<{
        id: number; jour: string;
        heure_debut: string; heure_fin: string;
        libelle_matiere: string; code_matiere: string;
        formateur_nom: string; salle: string; semestre: string;
      }>;
      conduite: {
        score: number;
        niveau: 'Excellent' | 'Bon' | 'Passable' | 'Insuffisante';
        nb_positives: number; nb_negatives: number;
        recent: Array<{
          id: number; date: string | null;
          categorie: string; type: string;
          poids: number; commentaire: string;
        }>;
      };
      paiements: {
        resume: {
          a_jour_global: boolean; nb_periodes: number;
          nb_a_jour: number; solde_total: string;
        };
        detail: Array<{
          periode: string; periode_label: string;
          est_a_jour: boolean;
          frais_attendus: string; frais_payes: string; solde_du: string;
          saisie_manuelle: boolean;
        }>;
      };
      resultats: Array<{
        semestre: number;
        moyenne: string | null;
        credits_obtenus: number; credits_requis: number;
        valide: boolean; doit_rattraper: boolean;
        mention: string; rang: number | null; effectif_classe: number | null;
        session_validation: string; annee_academique: string;
      }>;
      presences_par_mois: Array<{
        mois: string; total: number;
        present: number; absent: number; retard: number;
        justifie: number; incident: number;
      }>;
      presences_totaux_annee: {
        total: number; present: number; absent: number;
        retard: number; justifie: number; incident: number;
      };
      notes_devoirs: Record<string, Array<{
        matiere: string; code: string; semestre_global: number | null;
        note1: string | null; note2: string | null; moyenne: string | null;
        absent1: boolean; absent2: boolean; verrouille: boolean;
      }>>;
      notes_examens: Record<string, Array<{
        matiere: string; code: string; semestre_global: number | null;
        note: string | null; absent: boolean; verrouille: boolean;
      }>>;
      participation_examens: {
        NORMAL: Array<{
          semestre_global: number; nb_matieres: number;
          matieres: Array<{
            matiere_id: number; code: string; nom: string; semestre_global: number;
          }>;
        }>;
        RATTRAPAGE: Array<{
          semestre_global: number; nb_matieres: number;
          matieres: Array<{
            matiere_id: number; code: string; nom: string; semestre_global: number;
          }>;
        }>;
      };
    }, { matricule: string; depuis?: string; jusqua?: string }>({
      query: ({ matricule, ...params }) => ({
        url: `/vie-scolaire/etudiant/${matricule}/fiche/`,
        params,
      }),
      providesTags: (_r, _e, { matricule }) => [{ type: 'Seances', id: `fiche-${matricule}` }],
    }),

    // Dashboard absences
    dashboardAbsences: builder.query<{
      periode: { depuis: string; jusqua: string };
      classe: string | null;
      filtres?: {
        niveau: string | null; pole: string | null;
        option: string | null; semestre: string | null;
      };
      kpi: {
        total_pointages: number; taux_presence: number;
        present: number; absent: number; retard: number;
        justifie: number; incident: number;
      };
      top_absents: Array<{ matricule: string; nb_absences: number; total_seances: number; taux_absence: number }>;
      semaines: Array<{ debut: string; taux: number; total: number }>;
      par_classe: Array<{ classe: string; total: number; taux_presence: number }>;
    }, {
      depuis?: string; jusqua?: string;
      classe?: string; niveau?: string; pole?: string; option?: string;
      semestre?: 'impair' | 'pair';
    } | void>({
      query: (params) => ({ url: '/vie-scolaire/dashboard/absences/', params: params ?? undefined }),
      providesTags: ['Seances'],
    }),
  }),
});

export const {
  useListerSeancesQuery,
  useDetailAppelQuery,
  useValiderAppelMutation,
  useDetailAppelTroncCommunQuery,
  useValiderAppelTroncCommunMutation,
  useDetailAppelTroncCommunImpliciteQuery,
  useValiderAppelTroncCommunImpliciteMutation,
  useCreerSeanceALaVoleeMutation,
  useSupprimerSeanceMutation,
  useReporterSeanceMutation,
  useMatieresDisponiblesEdtQuery,
  useLazyMatieresDisponiblesEdtQuery,
  useHorairesStandardsQuery,
  useCopierEdtMutation,
  useDeplacerEdtMutation,
  useDeclarerIncidentMutation,
  useListerConduiteQuery,
  useCreerConduiteMutation,
  useListerEdtQuery,
  useCreerEdtMutation,
  useUpdateEdtMutation,
  useDeleteEdtMutation,
  useGenererSeancesMutation,
  useListerJustificationsQuery,
  useTraiterJustificationMutation,
  useMesSeancesFormateurQuery,
  useDashboardAbsencesQuery,
  useFicheEtudiantQuery,
  useLazyFicheEtudiantQuery,
} = vieScolaireApi;
